// src/components/Sidebar.js
//
// ═══════════════════════════════════════════════════════════════════════════
//  CHANGE LOG — 2026-09-19
//
//  THE MENU WAS RELOADING THE WHOLE APPLICATION ON EVERY CLICK.
//
//    Every item was a plain web link:
//        <a key={item.id} href={item.path} ...>
//
//    A plain <a href> tells the BROWSER "throw this page away and download a
//    new one". So clicking "Revenue" destroyed the entire running app and
//    rebuilt it from zero — every file fetched again, every piece of state
//    in memory wiped.
//
//    Two visible consequences:
//      • navigation felt slow, because it was a full page load every time
//      • DARK MODE SWITCHED ITSELF OFF, because the theme lives in React
//        state inside Header.js and a reload starts that state from scratch
//
//    Replaced with react-router's <Link to>, which swaps the page content in
//    place — no reload, no re-download, nothing in memory lost.
//    (Header.js was changed in the same pass to also SAVE the theme, so it
//    survives a deliberate browser refresh too.)
//
//  Two places were changed, and nothing else in this file:
//      1. the nav items inside NavPanel
//      2. the "Revenue" shortcut button on the dark strip
//
//  The menu list, icons, labels, paths, admin-only flags, collapse/overlay
//  behaviour and CSS class names are all exactly as they were.
// ═══════════════════════════════════════════════════════════════════════════

import { useState } from "react";
import { Link } from "react-router-dom";     // ADDED 2026-09-19
import { useAuth } from "../context/AuthContext";
import "../styles/sidebar.css";

const menu = [
  {
    group: null,
    items: [
      { id:"dashboard", label:"Dashboard", path:"/dashboard",
        icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> },
    ]
  },
  {
    group: "REPORTING",
    items: [
      { id:"revenue",   label:"Revenue",    path:"/revenue",
        icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="3" y1="20" x2="21" y2="20"/></svg> },
      { id:"emailsent", label:"Email Sent", path:"/emailsent",
        icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg> },
    ]
  },
  {
    group: "MANAGE",
    items: [
      { id:"offers",  label:"Offers",             path:"/offers",
        icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg> },
      { id:"servers", label:"Servers Management", path:"/servers",
        icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg> },
      { id:"esp",     label:"ESP Management",     path:"/esp",
        icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg> },
      { id:"domains", label:"Domain",             path:"/domains",
        icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg> },
    ]
  },
  {
    group: "DATA",
    items: [
      { id:"data", label:"Data Management", path:"/data",
        icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg> },
    ]
  },
  {
    group: "TOOLS",
    items: [
      { id:"users",      label:"User Management",       path:"/users",     adminOnly:true,
        icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
      { id:"affiliates", label:"Affiliates Management", path:"/affiliates", adminOnly:true,
        icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg> },

      { id:"links",      label:"Tracking Links",        path:"/links",
        icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg> },
      { id:"tools",      label:"Calc Email Setting",    path:"/tools",
        icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="12" y2="14"/></svg> },
    ]
  },
];

const NavPanel = ({ active, onClose }) => {
  const { isRole } = useAuth();
  return (
    <div className="sb-panel">
      <div className="sb-logo-area">
        <img src="/logo.png" alt="iDigital Hub" className="sb-logo-img"
          onError={e=>{e.target.style.display="none";e.target.nextSibling.style.display="flex";}}/>
        <div className="sb-logo-fallback" style={{display:"none"}}>
          <span className="sb-logo-i">i</span>
          <span className="sb-logo-rest">Digital Hub</span>
        </div>
      </div>
      <div className="sb-platform-hd">
        <span className="sb-platform-line"/>
        <span className="sb-platform-txt">PUBLISHER PLATFORM</span>
        <span className="sb-platform-line"/>
      </div>
      <nav className="sb-nav">
        {menu.map((sec,si)=>{
          const items = sec.items.filter(item=>!item.adminOnly||isRole("admin"));
          if (!items.length) return null;
          return (
            <div key={si} className="sb-sec">
              {sec.group && <div className="sb-grp">{sec.group}</div>}
              {items.map(item=>(
                // ── CHANGED 2026-09-19 ──────────────────────────────────
                // Was: <a key={item.id} href={item.path} ...>
                // <a href> made the browser reload the entire application
                // on every menu click, which is why navigation felt slow
                // and dark mode reset itself. <Link to> swaps the content
                // in place instead. Same className, same onClick, same
                // path — only the element type changed.
                <Link key={item.id} to={item.path}
                  className={`sb-item ${active===item.id?"on":""}`}
                  onClick={onClose}>
                  <span className="sb-icon">{item.icon}</span>
                  <span className="sb-lbl">{item.label}</span>
                </Link>
              ))}
            </div>
          );
        })}
      </nav>
    </div>
  );
};

export default function Sidebar({ active }) {
  // collapsed = true → only strip visible, content fills width
  // collapsed = false → strip + panel both visible, content pushed
  const [collapsed, setCollapsed] = useState(false);
  const [overlay,   setOverlay]   = useState(false);

  const handleHamburger = () => {
    if (!collapsed) {
      // Expanded → collapse (true layout change)
      setCollapsed(true);
      setOverlay(false);
    } else {
      // Already collapsed → show overlay popup (no layout change)
      setOverlay(o => !o);
    }
  };

  const handleCollapse = () => {
    if (!collapsed) {
      // Expand → collapse
      setCollapsed(true);
      setOverlay(false);
    } else {
      // Collapse → expand (true layout change)
      setCollapsed(false);
      setOverlay(false);
    }
  };

  const closeOverlay = () => setOverlay(false);

  return (
    <>
      {/* ── Dark strip — always visible ── */}
      <div className="sb-strip">
        <div className="sb-strip-top">
          {/* ☰ hamburger */}
          <button className="sb-strip-btn" onClick={handleHamburger} title="Menu">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6"  x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          {/* Revenue direct link
              CHANGED 2026-09-19: was <a href="/revenue"> — same full-reload
              problem as the menu items above. */}
          <Link to="/revenue" className="sb-strip-btn" title="Revenue" onClick={closeOverlay}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="20" x2="18" y2="10"/>
              <line x1="12" y1="20" x2="12" y2="4"/>
              <line x1="6"  y1="20" x2="6"  y2="14"/>
              <line x1="3"  y1="20" x2="21" y2="20"/>
            </svg>
          </Link>
        </div>
        {/* ⊲ / ⊳ collapse toggle */}
        <div className="sb-strip-bottom">
          <button className="sb-strip-btn" onClick={handleCollapse}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
            {collapsed
              ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
              : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            }
          </button>
        </div>
      </div>

      {/* ── Inline panel — only when NOT collapsed (true layout) ── */}
      {!collapsed && (
        <div className="sb-inline-panel">
          <NavPanel active={active} onClose={()=>{}}/>
        </div>
      )}

      {/* ── Overlay panel — only when collapsed + hamburger clicked ── */}
      {collapsed && overlay && (
        <>
          <div className="sb-overlay-bg" onClick={closeOverlay}/>
          <div className="sb-overlay-panel">
            <NavPanel active={active} onClose={closeOverlay}/>
          </div>
        </>
      )}
    </>
  );
}
